import { paymentReferenceTypes } from 'client/Interfaces/Draft/FieldsInterface'
import dayjs from 'dayjs'
import { ActionCreator } from 'typescript-fsa'

import * as documentApi from '../api/document'
import { getDocument } from '../api/document'
import * as api from '../api/estimate'
import { deleteEstimateEvent } from '../api/events'
import { getNextNumber, InvoicenumberResponseInterface } from '../api/invoiceNumber'
import { PremiumFeatureType } from '../components/Modal/PremiumFeatureIntermediaryModal'
import { Analytics } from '../Global/Analytics'
import {
  AnalyticsObject,
  AnalyticsType,
  Category,
  Events,
  Placement,
  PremiumModalOrigin,
  Result,
  trackingFields,
} from '../Global/AnalyticsEvents'
import { CustomerInterface } from '../Interfaces/CustomerInterface'
import { DocumentTypes, DraftInterface } from '../Interfaces/Draft/DraftInterface'
import { EstimateInterface } from '../Interfaces/Invoice/InvoiceInterface'
import { SendEventRequestInterface } from '../Interfaces/SendEventInterface'
import { StateInterface } from '../Interfaces/StateInterface'
import { QuotaTypes } from '../Interfaces/Subscription'
import { calculateDaysFromStartDate } from '../Other/functions/dateFunctions'
import { constructDocumentFileName, downloadPDF } from '../Other/functions/documentFunctions'
import { getFieldDefaultValue, handleFieldsBeforeSave, isFieldVisible } from '../Other/functions/modelFunctions'
import { generateReferenceNumber, generateStructuredCommunication } from '../Other/functions/referenceNumberFunctions'
import { Immutable } from '../Other/Immutable'
import { doesCurrentPlanSupportFeature } from '../reducers/Subscription/companySubscription'
import { EstimateActions } from './actionTypes'
import { getCustomers } from './customer'
import { getInvoiceNumberSequences } from './documentNumber'
import {
  changeDraft,
  changeLines,
  loadVitalsForEditor,
  navigateToEditor,
  replacePaymentMethodsFromSettings,
  updateCustomerNumberFromSettings,
  updateDraftCompanyFromCompany,
  updateDraftCompanyFromSettings,
  updateDraftCustomer,
  updateDraftCustomerFromCustomers,
  updateDraftFieldsFromSettings,
  updateEstimateNumber,
  updateInvoiceColumnsFromInvoiceType,
  updateInvoiceNumber,
  updatePaymentReferenceFromSettings,
  updateReferenceNumber,
} from './draft'
import { actionCreator, actionCreatorAsync, AsyncAction } from './index'
import {
  loadingEditorVitals,
  navigateAddModal,
  navigateRemoveModal,
  setNavigation,
  setNavigationPath,
} from './navigation'
import { hideGeneratingDocumentSpinner, showGeneratingDocumentSpinner } from './ongoing'

const moment = require('moment')

export const NAVIGATION_FROM_ESTIMATE = 'create-from-estimate'

function _createDeleteEstimatePayload(): SendEventRequestInterface {
  return {
    event: 'deleted',
    timestamp: new Date().toISOString(),
  }
}

export const deleteEstimate = (id: number) => {
  return async (dispatch, getState) => {
    await deleteEstimateEvent(id, _createDeleteEstimatePayload())
    dispatch(setNavigationPath('/reactEstimateList'))
  }
}

export const prepareEstimateForEdit: ActionCreator<number> = actionCreator(EstimateActions.EDIT_ESTIMATE)

export function editEstimate(id: number) {
  Analytics.trackEvent(`${Events.openDocument}`, {
    type: AnalyticsType.action,
    category: Category.document,
    result: Result.success,
    placement: Placement.estimates,
    documentType: AnalyticsObject.estimate,
    documentID: id,
  })

  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async () => {
        await Promise.all([
          dispatch(getCustomers()),
          dispatch(getEstimate(id)),
          loadVitalsForEditor(dispatch, getState()),
        ])
        dispatch(prepareEstimateForEdit(id))
        // update estimate with company, customer
        const { settings, company, draft } = getState()
        dispatch(updateCustomerNumberFromSettings(settings))
        dispatch(updateDraftCompanyFromSettings(settings))
        dispatch(updateDraftFieldsFromSettings(settings))
        dispatch(updateDraftCompanyFromCompany(company))
        if (!getState().customers.collection) {
          await dispatch(getCustomers())
        }
        const customer: CustomerInterface = getState().customers.collection?.find((c) => c.id === draft?.receiver?.id)
        if (customer) {
          dispatch(updateDraftCustomer(customer, true))
        }
      }),
    )
    navigateToEditor(dispatch, getState)
  }
}

export function getEstimate(id: number): AsyncAction<EstimateInterface> {
  return actionCreatorAsync<EstimateInterface>(EstimateActions.GET_ESTIMATE, async () => api.getEstimate(id))
}

export function saveEstimate(estimate: DraftInterface) {
  return actionCreatorAsync<DraftInterface>(EstimateActions.SAVE_ESTIMATE, async () => api.saveEstimate(estimate))
}

export const updateEstimate = (estimate: DraftInterface) => {
  const { estimateId, document } = estimate
  Analytics.trackEvent(Events.editDocument, {
    type: AnalyticsType.action,
    category: Category.document,
    result: Result.success,
    placement: Placement.editor,
    documentType: AnalyticsObject.estimate,
    documentId: estimateId,
  })
  return async (dispatch, getState) => {
    estimate.id = estimateId
    estimate = handleEstimateBeforeSave(estimate, getState().settings)
    await dispatch(saveEstimate(estimate))
    return dispatch(actionCreatorAsync(EstimateActions.SET_ESTIMATE, async () => getDocument(document, estimateId)))
  }
}

export const updateEstimateAndClose = (estimate: DraftInterface) => {
  return async (dispatch) => {
    dispatch(showGeneratingDocumentSpinner())
    await dispatch(updateEstimate(estimate))
    dispatch(hideGeneratingDocumentSpinner())
    const path = `/reactEstimateList/estimate/${estimate.id}`
    dispatch(
      setNavigation({
        path,
        app: {
          mod: 'invoices',
          'tabs-invoices': 'estimates',
          estimate: estimate.id,
        },
      }),
    )
  }
}

const handleEstimateBeforeSave = (estimate, settings) => {
  delete estimate.estimateId
  estimate = handleFieldsBeforeSave(estimate, settings)
  return estimate
}

export const downloadEstimatePdfById = (id: number) => {
  return async (dispatch, getState) => {
    const { estimate } = getState()
    const estimateDoc = estimate?.estimatedocument
    const fileName = constructDocumentFileName(estimateDoc) as string
    return downloadPDF(DocumentTypes.ESTIMATE, id, `${fileName}.pdf`)
  }
}

export const updateEstimateAndDownloadPdf = (estimateToSave: DraftInterface) => {
  return async (dispatch, getState) => {
    const id = estimateToSave?.estimateId ? estimateToSave?.estimateId : estimateToSave?.id
    const { settings } = getState()
    const docType = DocumentTypes.ESTIMATE
    const invoiceTab = `${docType}s`
    const module = settings?.modules?.find((mod) => mod?.name === 'reactEstimateList' && !mod?.hidden)
    const reactPath = `/reactEstimateList/estimate/${id}`
    const path = module ? reactPath : '/invoices'
    dispatch(showGeneratingDocumentSpinner())
    await dispatch(updateEstimate(estimateToSave))
    await dispatch(downloadEstimatePdfById(id))
    dispatch(hideGeneratingDocumentSpinner())
    dispatch(
      setNavigation({
        path,
        app: {
          mod: 'invoices',
          'tabs-invoices': invoiceTab,
          estimate: id,
        },
      }),
    )
    Analytics.trackEvent(`${Events.sendDocument}`, {
      type: AnalyticsType.action,
      category: Category.document,
      result: Result.success,
      placement: Placement.preview,
      documentType: AnalyticsObject.estimate,
      deliveryType: trackingFields?.deliveryType?.pdf,
      documentID: id,
    })
  }
}

export const createInvoiceDraftFromEstimate = (documentId: number) => {
  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async () => {
        await loadVitalsForEditor(dispatch, getState())
        const draft = await prepareInvoiceDraftFromEstimate(documentId, getState())
        dispatch(changeDraft(draft))
        dispatch(changeLines(getState().draft.lines))
        await dispatch(getInvoiceNumberSequences())
        dispatch(updateInvoiceNumber(getState().invoiceNumbers.active))
        const { settings, company, config } = getState()
        dispatch(updateCustomerNumberFromSettings(settings))
        dispatch(updateDraftCompanyFromSettings(settings))
        dispatch(updateDraftFieldsFromSettings(settings))
        dispatch(updateDraftCompanyFromCompany(company))
        dispatch(replacePaymentMethodsFromSettings(settings))
        dispatch(updatePaymentReferenceFromSettings(getState().settings))
        dispatch(updateInvoiceColumnsFromInvoiceType(config))
        const state = getState()
        const fields = state.draft.fields
        if ([paymentReferenceTypes.SPY_FI, paymentReferenceTypes.SPY_BE].includes(fields.paymentReferenceType.value)) {
          dispatch(
            updateReferenceNumber(
              fields.paymentReferenceType.value === paymentReferenceTypes.SPY_FI
                ? generateReferenceNumber(fields.invoiceNumber.value)
                : generateStructuredCommunication(),
            ),
          )
        }
      }),
    )
    navigateToEditor(dispatch, getState)
  }
}

export const convertEstimateToDraft = (id: number) => {
  return async (dispatch, getState) => {
    const openEditorWithDraft = () => dispatch(createInvoiceDraftFromEstimate(id))
    const state = getState()
    const subscription = state.subscriptionManagement.companySubscription.subscription

    const openSubscriptionModal = () =>
      dispatch(
        navigateAddModal({
          component: 'PremiumPlanModal',
          props: {
            isOpen: true,
            origin: PremiumModalOrigin.ConvertEstimateToDraft,
            hideDowngradePlans: true,
            next: openEditorWithDraft,
          },
        }),
      )
    if (!getState().settings.isOneProductUser || doesCurrentPlanSupportFeature(subscription, QuotaTypes.ESTIMATES)) {
      Analytics.trackEvent(Events.newDocument, {
        category: trackingFields.category.draft,
        result: trackingFields.result.success,
        placement: trackingFields.placement.estimates,
        documentType: trackingFields.documentType.invoice,
        type: trackingFields.type.action,
        sourceDocumentID: id,
        sourceDocumentType: trackingFields.documentType.estimate,
      })
      openEditorWithDraft()
    } else {
      dispatch(
        navigateAddModal({
          component: 'PremiumFeatureIntermediaryModal',
          props: {
            isOpen: true,
            featureType: PremiumFeatureType.ESTIMATE_TO_INVOICE,
            next: openSubscriptionModal,
          },
        }),
      )
    }
  }
}

export const getEstimateCopy = (id: number) => {
  return actionCreatorAsync<EstimateInterface>(EstimateActions.COPY_ESTIMATE, async () => api.getEstimate(id))
}

export const copyEstimate = (id: number) => {
  return async (dispatch, getState) => {
    dispatch(
      loadingEditorVitals(async () => {
        await dispatch(getEstimateCopy(id))
        const nextEstimateNumber: InvoicenumberResponseInterface = await getNextNumber(DocumentTypes.ESTIMATE)
        const { settings, company, customers } = getState()
        await loadVitalsForEditor(dispatch, getState())
        dispatch(updateDraftCompanyFromSettings(settings))
        dispatch(updateDraftCompanyFromCompany(company))
        dispatch(updateDraftCustomerFromCustomers(customers.collection))
        await dispatch(updateEstimateNumber(nextEstimateNumber))
      }),
    )
    navigateToEditor(dispatch, getState)
  }
}

export const showEstimate = (id: number) => {
  return async (dispatch) => {
    dispatch(
      navigateAddModal({
        component: 'ShowEstimateModal',
        props: {
          content: documentApi.getDocumentHTML(DocumentTypes.ESTIMATE, id),
          onClose: () => dispatch(navigateRemoveModal('ShowEstimateModal')),
          estimateId: id,
        },
      }),
    )
  }
}

export const prepareInvoiceDraftFromEstimate = async (documentId: number, state: StateInterface) => {
  const estimate = (await documentApi.getDocument(DocumentTypes.ESTIMATE, documentId)) as EstimateInterface
  const draft = estimate.estimatedocument
  draft.id = 0
  const { settings, company } = state
  const { baseInvoiceNumber } = company
  const invoiceModel = settings.models.find((i) => i.type === 'invoice')
  const titleField = invoiceModel.fields.find((i) => i.name === 'title')
  const refNoField = invoiceModel.fields.find((i) => i.name === 'referenceNumber')
  const paymentSchedule = invoiceModel.fields.find((i) => i.name === 'paymentScheduleEnabled')
  const isGenerateRefNo = ['FI', 'AX'].includes(company.country)
  draft.document = DocumentTypes.INVOICE
  draft.model = 'models.invoice'
  draft.fields.title.value = titleField.default as string
  delete draft.fields.discount
  draft.fields.issueDate.value = moment().toISOString()
  const baseNumber: string = baseInvoiceNumber ? baseInvoiceNumber.toString() : '0'
  draft.fields.invoiceNumber = {
    name: 'invoiceNumber',
    visible: true,
    value: baseNumber,
  }
  draft.fields.interest = {
    name: 'interest',
    visible: isFieldVisible('interest', settings, 'invoice'),
    value: (getFieldDefaultValue('interest', settings, 'invoice') as number) || 0,
  }
  draft.fields.customerNumber = {
    name: 'customerNumber',
    visible: isFieldVisible('customerNumber', settings, 'invoice'),
  }
  draft.fields.template = {
    name: 'template',
    value: `${company.country}.${getFieldDefaultValue('template', settings, 'invoice')}`,
  }
  const refNo = isGenerateRefNo ? generateReferenceNumber(baseNumber) : ''
  if (refNoField) {
    draft.fields.referenceNumber = {
      name: 'referenceNumber',
      visible: refNoField.visible,
      value: refNo,
    }
  }
  draft.fields.approvedEstimateId = {
    name: 'approvedEstimateId',
    value: documentId,
  }
  const defaultPaymentTime = (getFieldDefaultValue('paymentTime', settings, 'invoice') as number) || 0
  draft.fields.paymentTime = {
    name: 'paymentTime',
    value: defaultPaymentTime,
    visible: isFieldVisible('paymentTime', settings, 'invoice') || false,
  }

  if (paymentSchedule) {
    draft.fields.paymentScheduleEnabled = {
      name: 'paymentScheduleEnabled',
      visible: paymentSchedule.visible,
      value: paymentSchedule.default === 'true',
    }
  }
  draft.fields.legalterms = {
    name: 'legalterms',
    visible: isFieldVisible('legalterms', settings, 'invoice'),
    value: getFieldDefaultValue('legalterms', settings, 'invoice') as string,
  }

  draft.fields.footerText = {
    name: 'footerText',
    visible: isFieldVisible('footerText', settings, 'invoice'),
    value: getFieldDefaultValue('footerText', settings, 'invoice') as string,
  }

  draft.fields.dueDate = {
    name: 'dueDate',
    visible: isFieldVisible('dueDate', settings, 'invoice'),
    value: calculateDaysFromStartDate(dayjs(), defaultPaymentTime).format(),
  }

  draft.fields.paymentReferenceType = {
    name: 'paymentReferenceType',
    value: getFieldDefaultValue('paymentReferenceType', settings, 'invoice') as paymentReferenceTypes,
  }

  const type = (getFieldDefaultValue('type', settings, 'invoice') as number) || 1
  draft.fields.type.value = Number(type)

  return Immutable.copy(draft)
}

export function setEstimateStatus(estimateId, statusType) {
  return async () => {
    await api.setEstimateStatus(estimateId, statusType)
  }
}
